// Canonical sport / activity / workout-block icon mapping (lucide-react).
//
// Single source of truth for the emoji→icon migration. Every client (web here,
// mobile mirrors this in its own lucide-react-native module) should key off the
// same maps so a sport's glyph never drifts between screens.
//
// Icons are React components, so render them as <SportIcon sport="run" size={16} />
// rather than as a string. All standard lucide props (size, className, color,
// strokeWidth, ...) pass straight through.

import {
  WavesHorizontal as Waves, // lucide-react renamed the old `Waves` → `WavesHorizontal`
  Bike,
  Footprints,
  PersonStanding,
  Dumbbell,
  Moon,
  StickyNote,
  Flag,
  Mountain,
  MountainSnow,
  Leaf,
  Kayak,
  Activity,
  Volleyball,
  Eclipse,
  Triangle,
  Flame,
  Snowflake,
  Target,
  Zap,
} from 'lucide-react';

// --- Session / plan sport types (keyed by lowercase sport) ---
export const SPORT_ICON = {
  swim: Waves,
  bike: Bike,
  ride: Bike, // synonym for bike
  run: Footprints,
  gym: Dumbbell,
  strength: Dumbbell, // synonym for gym
  rest: Moon,
  note: StickyNote,
  race: Flag,
  other: Dumbbell,
};

export const SPORT_LABEL = {
  swim: 'Swim',
  bike: 'Bike',
  run: 'Run',
  gym: 'Gym',
  rest: 'Rest',
  note: 'Note',
  race: 'Race',
};

// --- Strava activity types (keyed by Strava's activity `type` string) ---
export const ACTIVITY_TYPE_ICON = {
  Run: Footprints,
  Ride: Bike,
  Swim: Waves,
  VirtualRun: Footprints,
  VirtualRide: Bike,
  Walk: PersonStanding,
  Hike: Mountain,
  WeightTraining: Dumbbell,
  Workout: Dumbbell,
  Yoga: Leaf,
  Rowing: Kayak,
  Kayaking: Kayak,
  StandUpPaddling: Waves,
  Surfing: Waves,
  Ski: MountainSnow,
  Snowboard: MountainSnow,
  IceSkate: Activity,
  Soccer: Volleyball,
  Tennis: Eclipse,
  Golf: Triangle,
};

// --- Workout block / interval types ---
export const WORKOUT_BLOCK_ICON = {
  warmup: Flame,
  activation: Flame,
  cooldown: Snowflake,
  stretch: Snowflake,
  mobility: Snowflake,
  strength: Dumbbell,
  main: Dumbbell,
  accessory: Target,
  circuit: Zap,
};

/**
 * Session/plan sport icon. `isRace` forces the race flag regardless of sport.
 * Falls back to the generic Dumbbell for unknown sports.
 */
export function SportIcon({ sport, isRace, ...props }) {
  const Icon = isRace ? Flag : SPORT_ICON[sport?.toLowerCase()] || Dumbbell;
  return <Icon {...props} />;
}

/**
 * Strava activity-type icon. Falls back to Footprints (run) for unknown types,
 * matching the previous emoji-map default.
 */
export function ActivityIcon({ type, ...props }) {
  const Icon = ACTIVITY_TYPE_ICON[type] || Footprints;
  return <Icon {...props} />;
}

/** Workout block/interval icon. Falls back to Dumbbell (main set). */
export function WorkoutBlockIcon({ block, ...props }) {
  const Icon = WORKOUT_BLOCK_ICON[block?.toLowerCase()] || Dumbbell;
  return <Icon {...props} />;
}
